import { simpleAlert } from "components/CommonLib/CommonLib";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, deleteDoc, doc, getDocs } from "firebase/firestore";
import { deleteObject, ref } from "firebase/storage";
import { useDispatch } from "react-redux";
import { auth, db, storage } from "../../firebase";
import AddModal from "./AddModal";
import CartSidebar from "./CartSidebar";
import EstimateCard from "./EstimateCard";
import EstimateLogin from "./EstimateLogin";
import FilterCard from "./FilterCard";
import HistoryModal from "./HistoryModal";
import { runExcelUpload } from "./uploadExcel";

const { useState, useEffect, useRef } = require("react");

const COMPANY_EMAIL = "therapscompany@gmail.com";
const PAGE_SIZE = 12;

const EstimateView = () => {
  const storesDispatch = useDispatch();
  const excelInputRef = useRef(null);
  const isFirst = useRef(true);

  const [isAuth, setIsAuth] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const [lastVisible, setLastVisible] = useState(PAGE_SIZE);

  const [allItems, setAllItems] = useState([]);

  // 필터링된 데이터 (화면에 표시)
  const [filteredItems, setFilteredItems] = useState(allItems);

  // 필터 상태
  const [filters, setFilters] = useState({
    itemType: "웨이트 기구",
    searchText: "",
    selectedSeries: "전체",
    selectedFormat: "전체",
  });

  const getItemType = (item) => item.itemType ?? "웨이트 기구";
  // 필터 적용 함수
  const applyFilters = () => {
    let result = allItems;
    // 0. 웨이트 / 기타 구분
    result = result.filter((item) => getItemType(item) === filters.itemType);
    // 1. 검색
    if (filters.searchText) {
      result = result.filter((item) =>
        item.name.toLowerCase().includes(filters.searchText.toLowerCase()),
      );
    }
    // 2·3. 기타 기자재가 아닐 때만 시리즈·양식 적용
    if (filters.itemType !== "기타 기자재") {
      if (filters.selectedSeries !== "전체") {
        result = result.filter((item) => item.series === filters.selectedSeries);
      }
      if (filters.selectedFormat !== "전체") {
        result = result.filter((item) => item.format === filters.selectedFormat);
      }
    }
    setFilteredItems(result);
    setLastVisible(Math.min(PAGE_SIZE, result.length));
  };

  // 더보기 로드
  const loadMoreItems = () => {
    setLastVisible((prev) => Math.min(prev + PAGE_SIZE, filteredItems.length));
  };

  // 장바구니에 추가
  const addToCart = (item) => {
    const existing = cartItems.find((cartItem) => cartItem.id === item.id);
    if (existing) {
      // 중복이면 수량 증가
      setCartItems(
        cartItems.map((cartItem) =>
          cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem,
        ),
      );
    } else {
      // 신규면 1개 세팅
      setCartItems([...cartItems, { ...item, quantity: 1 }]);
    }
  };

  const removeFromCart = (itemId) => {
    const existing = cartItems.find((item) => item.id === itemId);
    if (existing.quantity > 1) {
      setCartItems(
        cartItems.map((item) =>
          item.id === itemId ? { ...item, quantity: item.quantity - 1 } : item,
        ),
      );
    } else {
      // 수량이 1이면 제거
      setCartItems(cartItems.filter((item) => item.id !== itemId));
    }
  };
  // 기자재 수정
  const onEditItem = (item) => {
    setSelectedItem(item);
    setIsAddModalOpen(true);
  };
  // 기자재 삭제
  const onDeleteItem = async (item) => {
    try {
      // 1. Firestore 문서 삭제
      await deleteDoc(doc(db, "deploy", "estimate", "items", item.id));
      // 2. Storage 이미지 삭제
      if (item.image) {
        try {
          const imageRef = ref(storage, item.image);
          await deleteObject(imageRef);
        } catch (err) {
          console.log("이미지 삭제 실패 (파일이 없을 수 있음):", err);
        }
      }
      fetchItems();
    } catch (err) {
      console.error("삭제 실패:", err);
      simpleAlert(storesDispatch, "ERROR", "삭제에 실패했습니다.");
    }
  };

  // 엑셀로 한번에 업로드
  const onExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await runExcelUpload(file);
      if (!result.ok) {
        simpleAlert(storesDispatch, "ERROR", result.message);
        return;
      }
      simpleAlert(storesDispatch, "알림", result.message);

      await fetchItems();
    } catch (err) {
      console.error("엑셀 업로드 실패:", err);
      simpleAlert(storesDispatch, "ERROR", "엑셀 업로드에 실패했습니다.");
    } finally {
      e.target.value = "";
    }
  };

  // firebase에서 아이템 목록 가져오기
  const fetchItems = async () => {
    try {
      const itemsRef = collection(db, "deploy", "estimate", "items");
      const snapshot = await getDocs(itemsRef);

      const items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      items.sort((a, b) => {
        const seriesCompare = String(a.series ?? "").localeCompare(String(b.series ?? ""), "ko", {
          numeric: true,
        });
        if (seriesCompare !== 0) return seriesCompare;

        return String(a.model ?? "").localeCompare(String(b.model ?? ""), "ko", { numeric: true });
      });
      // 순수 이름 순서
      // items.sort((a, b) =>
      //   String(a.name ?? "").localeCompare(String(b.name ?? ""), "ko", { numeric: true }),
      // );
      setAllItems(items);
    } catch (err) {
      console.log("기자재 목록 로드 실패:", err);
    }
  };

  const onLoadHistory = (history) => {
    const currentIds = new Set(allItems.map((item) => item.id));
    const validItems = (history.items || []).filter((his) => currentIds.has(his.id));
    const removedCount = (history.items || []).length - validItems.length;
    if (validItems.length === 0) {
      simpleAlert(
        storesDispatch,
        "알림",
        "현재 판매 목록에 남아있는 품목이 없어 불러올 수 없습니다.",
      );
      return;
    }
    // 현재 데이터와 동기화
    const syncedItems = validItems.map((his) => {
      const current = allItems.find((item) => item.id === his.id);

      return {
        ...his,
        name: current?.name ?? his.name,
        model: current?.model ?? his.model,
        series: current?.series ?? his.series,
        format: current?.format ?? his.format,
        image: current?.image ?? his.image,
        price: Number(current?.price ?? his.price) || 0,
      };
    });

    setCartItems(syncedItems);
    if (removedCount > 0) {
      simpleAlert(storesDispatch, "알림", `삭제된 품목 ${removedCount}개는 제외하고 불러왔습니다.`);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.log("Firebase 로그아웃 실패:", err);
    }
    sessionStorage.clear();
    setIsAuth(false);
    setIsAdmin(false);
  };

  // 로그인 상태 확인
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAdmin(user.email === COMPANY_EMAIL);
      }
    });
    return () => unsubscribe();
  }, []);

  // 탭 변경될 때마다 자동 적용
  useEffect(() => {
    // 첫 렌더링 패스
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    applyFilters();
  }, [filters.itemType]);

  // 필터 적용
  useEffect(() => {
    applyFilters();
  }, [allItems]);

  // firebase에서 아이템 목록 가져오기
  useEffect(() => {
    fetchItems();
  }, []);

  // 로그인 체크
  useEffect(() => {
    const branch = sessionStorage.getItem("branch");
    const signature = sessionStorage.getItem("branch_sig");
    if (branch && signature) {
      setIsAuth(true);
    }
  }, []);

  return !isAuth ? (
    <EstimateLogin
      onSuccess={() => {
        setIsAuth(true);
      }}
    />
  ) : (
    <div style={{ display: "relative" }}>
      <AddModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedItem(null);
        }}
        onSuccess={() => {
          fetchItems();
        }}
        item={selectedItem}
      />
      <div
        style={{
          display: "flex",
          padding: "20px",
          gap: "20px",
          height: "100vh",
          boxSizing: "border-box",
        }}
      >
        {/* 왼쪽 필터 */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <FilterCard
            filters={filters}
            setFilters={setFilters}
            allItems={allItems}
            applyFilters={applyFilters}
          />
          <HistoryModal
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            onLoadHistory={(history) => {
              onLoadHistory(history);
            }}
          />

          {/* 견적서 */}
          <CartSidebar
            cartItems={cartItems}
            onRemoveItem={removeFromCart}
            onClear={() => setCartItems([])}
            onAddItem={addToCart}
            setIsAuth={setIsAuth}
            setIsHistoryModalOpen={setIsHistoryModalOpen}
          />
        </div>
        {/* 오른쪽 기구 목록 */}
        <div style={{ flex: 1, position: "relative" }}>
          {/* 로그아웃 버튼 */}
          <button
            onClick={logout}
            style={{
              position: "fixed",
              top: 10,
              right: 10,
              padding: "8px 16px",
              backgroundColor: "#fff",
              border: "1px solid #aaa",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              zIndex: 10,
            }}
          >
            <span>➡️</span>
            {sessionStorage.getItem("branch")} 로그아웃
          </button>
          {/* 본사 전용: 기자재 추가 버튼 */}
          {isAdmin && (
            <>
              <button
                onClick={() => excelInputRef.current?.click()}
                style={{
                  position: "fixed",
                  bottom: 58,
                  right: 10,
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "#16a34a",
                  color: "#fff",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  zIndex: 10,
                }}
              >
                +
              </button>
              <input
                ref={excelInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={onExcelUpload}
              />

              <button
                onClick={() => {
                  setSelectedItem(null);
                  setIsAddModalOpen(true);
                }}
                style={{
                  position: "fixed",
                  bottom: 10,
                  right: 10,
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: "#10134F",
                  color: "#fff",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                +
              </button>
            </>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: "20px",
            }}
          >
            {filteredItems.slice(0, lastVisible).map((item) => (
              <EstimateCard
                key={item.id}
                item={item}
                onAddToCart={addToCart}
                isAdmin={isAdmin}
                onEditItem={onEditItem}
                onDeleteItem={onDeleteItem}
              />
            ))}
          </div>
          {lastVisible < filteredItems.length && (
            <button
              onClick={loadMoreItems}
              style={{
                padding: "8px 24px",
                margin: "20px auto",
                backgroundColor: "#10134F",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              더보기
            </button>
          )}

          {/* 결과 없을 때 메시지 */}
          {filteredItems.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "40px",
                color: "#999",
              }}
            >
              검색 결과가 없습니다
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default EstimateView;
